import React from "react";
import styled from "styled-components/macro";

import ActionModal from "components/ActionModal";
import Link from "ui/Link";
import { openZendeskMessenger } from "components/LiveChat/LiveChat";
import Typography from "ui/Typography";
import SelectUserType from "../SelectUserType";
import ShockedFaceSVG from "images/shocked_face.svg";
import WaitClockSVG from "images/clock-orange.svg";
import { HelpLinks } from "utils/helpLinks";
import { useNavigate } from "react-router-dom";

// These need to map 1:1 with USER_FACING_ERROR_CODES defined in auth_controller.rb
export enum ErrorMessages {
  INVALID_DOMAIN = "invalid_domain",
  PENDING_ACCOUNT_LIVE_SUPPORT = "pending_account_live_support",
  PENDING_ACCOUNT = "pending_account",
  REJECTED_ACCOUNT = "rejected_account",
  DISABLED_ACCOUNT = "disabled_account",
  GENERIC_ERROR = "generic_error",
  ACCOUNT_NOT_FOUND = "account_not_found",
}

const $Disclaimer = styled(Typography).attrs({
  color: "ColorSpace500",
  size: "tooltip",
  textAlign: "center",
})`
  margin-top: 1rem;
`;

function ContactUsButton(props: { analyticsName: string }) {
  return (
    <ActionModal.PrimaryButton
      {...props}
      href={HelpLinks.NEW_REQUEST}
      rel="noopener"
      target="_blank"
    >
      Contact us
      <span className="visually-hidden">Opens in a new tab.</span>
    </ActionModal.PrimaryButton>
  );
}

export default function UserLoginModal(props: { type: string | null }) {
  const [showModal, toggleShowModal] = React.useState(true);
  switch (props.type) {
    case ErrorMessages.INVALID_DOMAIN:
      // isPersonalDomain
      return (
        <InvalidDomainModal
          showModal={showModal}
          toggleShowModal={toggleShowModal}
        />
      );
    case ErrorMessages.PENDING_ACCOUNT_LIVE_SUPPORT:
      // (!isApprovedDomain && isLoginAccountAbsent)
      return (
        <RequestApprovalDomainModal
          showModal={showModal}
          toggleShowModal={toggleShowModal}
        />
      );
    case ErrorMessages.PENDING_ACCOUNT:
      // isAccountPending
      return (
        <PendingApprovalDomainModal
          showModal={showModal}
          toggleShowModal={toggleShowModal}
        />
      );
    case ErrorMessages.REJECTED_ACCOUNT:
      // isAccountRejected
      return (
        <RejectionModal
          showModal={showModal}
          toggleShowModal={toggleShowModal}
        />
      );
    case ErrorMessages.DISABLED_ACCOUNT:
      // isAccountDisabled
      return (
        <DisabledAccountModal
          showModal={showModal}
          toggleShowModal={toggleShowModal}
        />
      );
    case ErrorMessages.GENERIC_ERROR:
      // generic catch all
      return (
        <UnknownErrorModal
          showModal={showModal}
          toggleShowModal={toggleShowModal}
        />
      );
    case ErrorMessages.ACCOUNT_NOT_FOUND:
      return (
        <UnknownAccount
          showModal={showModal}
          toggleShowModal={toggleShowModal}
        />
      );
    default:
      // @assert - The only case left is of an approved account
      // that is (isApprovedDomain || isAccountApproved)
      return null;
  }
}

interface Props {
  showModal: boolean;
  toggleShowModal: (updateState: boolean) => void;
}

function InvalidDomainModal({ showModal, toggleShowModal }: Props) {
  const navigate = useNavigate();

  return (
    <ActionModal
      analyticsName="viewed_invalid_domain_modal"
      close={{
        analyticsName: "clicked_close_on_invalid_domain_modal",
        onClick: () => toggleShowModal(false),
      }}
      image={<img alt="" src={ShockedFaceSVG} />}
      title="We&rsquo;re having trouble creating your account."
      subtitle="Please use your school email address (K-12, USA) to sign up."
      show={showModal}
    >
      <ActionModal.Footer>
        <ActionModal.PrimaryButton
          analyticsName="clicked_try_again_on_invalid_domain_modal"
          onClick={() => {
            navigate(0);
            toggleShowModal(false);
          }}
        >
          Try Again
        </ActionModal.PrimaryButton>
      </ActionModal.Footer>
    </ActionModal>
  );
}

function RequestApprovalDomainModal({ showModal, toggleShowModal }: Props) {
  return (
    <ActionModal
      analyticsName="viewed_unknown_email_modal"
      close={{
        analyticsName: "clicked_close_on_unknown_email_modal",
        onClick: () => toggleShowModal(false),
      }}
      image={<img alt="" src={ShockedFaceSVG} />}
      title="We&rsquo;re having trouble creating your account."
      subtitle="Start a chat with us to answer a few questions and get connected to our live support team for assistance."
      show={showModal}
    >
      <ActionModal.Footer>
        <ActionModal.PrimaryButton
          analyticsName="clicked_contact_live_support_on_unknown_email_modal"
          onClick={() => {
            toggleShowModal(false);
            openZendeskMessenger();
          }}
        >
          Start Chat
        </ActionModal.PrimaryButton>
        <$Disclaimer>
          If you don&rsquo;t connect with us via the chat, we&rsquo;ll follow up
          with you by email with a few questions.
        </$Disclaimer>
      </ActionModal.Footer>
    </ActionModal>
  );
}

function PendingApprovalDomainModal({ showModal, toggleShowModal }: Props) {
  return (
    <ActionModal
      analyticsName="viewed_pending_approval_modal"
      close={{
        analyticsName: "clicked_close_on_pending_approval_modal",
        onClick: () => toggleShowModal(false),
      }}
      image={<img alt="" src={WaitClockSVG} />}
      title="Thanks for being patient."
      subtitle="We&rsquo;re in the process of verifying your account and will email you once this is complete."
      show={showModal}
    >
      <ActionModal.Footer>
        <ActionModal.PrimaryButton
          analyticsName="clicked_ok_on_pending_approval_modal"
          onClick={() => toggleShowModal(false)}
        >
          OK
        </ActionModal.PrimaryButton>
      </ActionModal.Footer>
    </ActionModal>
  );
}

const $ButtonPrompt = styled(Typography).attrs({
  size: "bodySansSerif",
})`
  margin-bottom: 1rem;
`;

function RejectionModal({ showModal, toggleShowModal }: Props) {
  return (
    <ActionModal
      analyticsName="viewed_account_rejected_modal"
      close={{
        analyticsName: "clicked_close_on_account_rejected_modal",
        onClick: () => toggleShowModal(false),
      }}
      image={<img alt="" src={ShockedFaceSVG} />}
      title="Sorry!"
      subtitle="We are unable to verify your school email address."
      show={showModal}
    >
      <ActionModal.Footer>
        <$ButtonPrompt>To get in touch:</$ButtonPrompt>
        <ContactUsButton analyticsName="clicked_contact_us_on_account_rejected_modal" />
        <$Disclaimer>
          You can always{" "}
          <Link
            analyticsName="clicked_learn_more_on_account_rejected_modal"
            href="https://www.along.org/"
            opensInNewTab
          >
            learn more about Along
          </Link>
        </$Disclaimer>
      </ActionModal.Footer>
    </ActionModal>
  );
}

function DisabledAccountModal({ showModal, toggleShowModal }: Props) {
  return (
    <ActionModal
      analyticsName="viewed_account_disabled_modal"
      close={{
        analyticsName: "clicked_close_on_account_disabled_modal",
        onClick: () => toggleShowModal(false),
      }}
      image={<img alt="" src={ShockedFaceSVG} />}
      title="Sorry!"
      subtitle="Your account has been disabled. Contact us if you have questions."
      show={showModal}
    >
      <ActionModal.Footer>
        <ContactUsButton analyticsName="clicked_contact_us_on_account_disabled_modal" />
      </ActionModal.Footer>
    </ActionModal>
  );
}

function UnknownErrorModal({ showModal, toggleShowModal }: Props) {
  return (
    <ActionModal
      analyticsName="viewed_unknown_error_modal"
      close={{
        analyticsName: "clicked_close_on_unknown_error_modal",
        onClick: () => toggleShowModal(false),
      }}
      image={<img alt="" src={ShockedFaceSVG} />}
      title="Sorry!"
      subtitle="We&rsquo;re having some technical difficulties."
      show={showModal}
    >
      <ActionModal.Footer>
        <ContactUsButton analyticsName="clicked_contact_us_on_unknown_error_modal" />
      </ActionModal.Footer>
    </ActionModal>
  );
}

const $ModalBody = styled(ActionModal.Body)`
  text-align: center;
`;

function UnknownAccount({ showModal = false, toggleShowModal }: Props) {
  return (
    <ActionModal
      analyticsName="viewed_unknown_account_modal"
      close={{
        analyticsName: "clicked_close_on_unknown_account_modal",
        onClick: () => toggleShowModal(false),
      }}
      image={<img alt="" src={ShockedFaceSVG} />}
      title="We don’t recognize this account"
      show={showModal}
    >
      <$ModalBody>
        <SelectUserType />
      </$ModalBody>
    </ActionModal>
  );
}
